import React, {useCallback, useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import InfiniteScroll from 'react-infinite-scroll-component';
import {useDispatch, useSelector} from 'react-redux';
import {IconButton} from '@material-ui/core';
import FormControl from '@material-ui/core/FormControl';
import {useHistory} from 'react-router-dom';
import moment from 'moment';
import {createPortal} from 'react-dom';
import {debounce} from 'lodash';
import cn from 'classnames';
import ButtonPrimary from 'components/buttons/ButtonPrimary/styles';
import {
  DashboardField,
  DashboardFieldInner,
  DashboardFieldLabel,
  DashboardFields,
  FieldLabel
} from 'routes/App/dashboard/styles';
import {NewLineBlock, OrderEta} from 'routes/App/ordersV2/OrdersList/styles';
import {
  CHANGE_STATUS_HISTORY,
  CLEAR_ORDERS,
  GET_ORDER,
  GET_ORDERS,
  SET_FILTER,
  SET_SEARCH,
  SET_SORTING
} from 'store/ordersV2/orderList/action';
import IconPrint from 'assets/IconPrint';
import {
  DELIVERY_PROVIDER_TYPE,
  ORDER,
  FILTER_ORDER_SOURCES,
  ORDER_MISC_MANY,
  ORDER_PAYMENT_METHODS,
  ORDER_SOURCE,
  ORDER_STATUS,
  ORDER_TYPE,
  ORDER_TYPES,
  ORDER_TYPES_ALL,
  FILTER_ORDER_SOURCE,
  ORDER_CHANNELS,
  ORDER_FULFILLMENT_NAME
} from 'helpers/constants/orders_v2';
import StatusButton from 'routes/App/ordersV2/OrdersList/components/StatusButton';
import {FlexContainer, FlexItem} from 'components/layout';
import {groupDigital} from 'helpers/functionUtils/groupByDigital';
import SelectField from 'components/SelectField';
import DatepickerModule from 'components/DatepickerModule';
import {
  RUN_GET_ACTIVE_ORDERS_JOB,
  STOP_GET_ACTIVE_ORDERS_JOB
} from 'store/global/ordersV2/action';
import {phoneFormatForView} from 'helpers/functionUtils/helpFunctions';
import ButtonScrollTop from 'components/buttons/ButtonScrollTop';
import IconPickUpDefault from 'assets/icon-pickup-default.svg';
import IconCurbsidePickup from 'assets/icon-curbside-pickup.svg';
import IconDinein from 'assets/icon-dinein.svg';
import {OrdersTableWrapper} from 'routes/App/ordersV2/components/OrdersTable/styles';
import {FEATURE_RESTRICTION_IDS} from 'helpers/constants/restrictions';
import {
  checkIsBaDPOS,
  checkIsRefundOrder,
  getOrderPaymentMethodLabel,
  getOrderIdDisplay,
  shouldShowOrderPrice,
  shouldShowTipsAndTotal
} from 'helpers/functionUtils/ordersUtilsV2';
import IconRestaurantDelivery from 'assets/icon-restaurant-delivery.svg';
import {OPEN_CANCELLATION_CONFIRMATION_MODAL_V2} from 'store/global/modals/action';
import SearchField from 'components/fields/SearchField';
import OrdersTableComponent from 'components/OrdersTableComponentV2';
import {TableText} from 'components/TableComponent/styles';
import SelectFiltersOrderV2 from 'components/SelectField/SelectFiltersOrderV2';
import {CLEAR_ORDER} from 'store/ordersV2/orderDetails/action';
import OrderSourceIcon from 'routes/App/ordersV2/components/OrderSourceIcon';
import HoldCountdownPill from 'routes/App/ordersV2/components/HoldCountdownPill';
import {FulfilmentCell} from 'routes/App/ordersV2/components/HoldCountdownPill/styles';

const getFieldsConfig = (
  onStatusChange,
  onOpenDetailsClick,
  onPrintClick,
  changeStatusLoading,
  showOtterBrandName,
  allowedServiceFeatures
) => [
  {
    title: 'ETA',
    onCellClick: onOpenDetailsClick,
    sortable: true,
    fieldName: 'CookingEndTime.Local',
    renderer: (data) => (
      <OrderEta>
        {checkIsRefundOrder(data) && <span className='IconRefundOrder' />}
        {moment(`${data[ORDER.DATE]}Z`).utcOffset(0).format('MMMM D, h:mm A')}
      </OrderEta>
    )
  },
  ...(showOtterBrandName
    ? [
        {
          title: 'Location',
          onCellClick: onOpenDetailsClick,
          renderer: (data) => <div>{data[ORDER.LOCATION]?.storeName || ''}</div>
        }
      ]
    : []),
  {
    title: 'Order ID',
    onCellClick: onOpenDetailsClick,
    fieldName: ORDER.INVOICE_ID,
    renderer: (data) => <div>{getOrderIdDisplay(data)}</div>
  },
  {
    title: 'Customer',
    onCellClick: onOpenDetailsClick,
    renderer: (data) => (
      <FlexContainer className='TableCenter'>
        <NewLineBlock className='wordWrap'>
          {data[ORDER.CUSTOMER]?.fullName || ''}
        </NewLineBlock>
        {data[ORDER.CUSTOMER]?.phone && (
          <NewLineBlock>
            <a
              href={`tel:${data[ORDER.CUSTOMER]?.phone}`}
              alt='customer-phone-number'
              onClick={(e) => e.stopPropagation()}
            >
              {phoneFormatForView(data[ORDER.CUSTOMER]?.phone)}
            </a>
          </NewLineBlock>
        )}
      </FlexContainer>
    )
  },
  {
    title: 'Order Source',
    onCellClick: onOpenDetailsClick,
    renderer: (data) => {
      return (
        <FlexContainer className='align-center justify-center'>
          <OrderSourceIcon order={data} />
        </FlexContainer>
      );
    }
  },
  {
    title: 'Fulfilment',
    onCellClick: onOpenDetailsClick,
    renderer: (data) => {
      return (
        <FlexContainer className='align-center orderProvider'>
          {(() => {
            if (
              data[ORDER.ORDER_TYPE] === ORDER_TYPE.DELIVERY &&
              data[ORDER.DELIVERY]?.provider === DELIVERY_PROVIDER_TYPE.DISPATCH
            ) {
              return (
                <FulfilmentCell>
                  <TableText>Sauce delivery</TableText>
                  <HoldCountdownPill
                    order={data}
                    serviceFeatures={allowedServiceFeatures}
                  />
                </FulfilmentCell>
              );
            }
            if (
              data[ORDER.ORDER_TYPE] === ORDER_TYPE.DELIVERY &&
              data[ORDER.DELIVERY]?.provider === DELIVERY_PROVIDER_TYPE.LOCATION
            ) {
              return <TableText>Self-delivery</TableText>;
            }
            if (
              data[ORDER.ORDER_TYPE] === ORDER_TYPE.DELIVERY &&
              data[ORDER.DELIVERY]?.provider ===
                DELIVERY_PROVIDER_TYPE.ORDER_PROVIDER &&
              data[ORDER.SOURCE] === ORDER_SOURCE.UBER
            ) {
              return <TableText>Uber delivery</TableText>;
            }
            if (
              data[ORDER.ORDER_TYPE] === ORDER_TYPE.DELIVERY &&
              data[ORDER.DELIVERY]?.provider ===
                DELIVERY_PROVIDER_TYPE.ORDER_PROVIDER &&
              data[ORDER.SOURCE] === ORDER_SOURCE.DOORDASH
            ) {
              return <TableText>DoorDash delivery</TableText>;
            }
            if (
              data[ORDER.ORDER_TYPE] === ORDER_TYPE.DELIVERY &&
              data[ORDER.DELIVERY]?.provider ===
                DELIVERY_PROVIDER_TYPE.ORDER_PROVIDER &&
              data[ORDER.SOURCE] === ORDER_SOURCE.GRUBHUB
            ) {
              return <TableText>GrubHub delivery</TableText>;
            }
            if (
              data[ORDER.ORDER_TYPE] === ORDER_TYPE.DELIVERY &&
              data[ORDER.DELIVERY]?.provider ===
                DELIVERY_PROVIDER_TYPE.ORDER_PROVIDER &&
              data[ORDER.SOURCE] === ORDER_SOURCE.SLICE
            ) {
              return <TableText>Slice delivery</TableText>;
            }
            if (
              data[ORDER.ORDER_TYPE] === ORDER_TYPE.DELIVERY &&
              data[ORDER.DELIVERY]?.provider ===
                DELIVERY_PROVIDER_TYPE.ORDER_PROVIDER &&
              data[ORDER.SOURCE] === ORDER_SOURCE.EZCATER
            ) {
              return <TableText>EZCater delivery</TableText>;
            }
            if (data[ORDER.ORDER_TYPE] === ORDER_TYPE.DINE_IN) {
              return <TableText>Dine in</TableText>;
            }
            return <TableText>Pickup</TableText>;
          })()}
        </FlexContainer>
      );
    }
  },
  {
    title: 'Amount',
    onCellClick: onOpenDetailsClick,
    renderer: (data) => {
      if (!shouldShowTipsAndTotal(data, allowedServiceFeatures)) {
        return <div />;
      }

      const amount = data[ORDER.AMOUNT];
      const paymentMethod = getOrderPaymentMethodLabel(
        data[ORDER.PAYMENT_METHOD]
      );
      const amountLabel = shouldShowOrderPrice(amount, data[ORDER.SOURCE])
        ? `$${groupDigital(Number(amount || 0).toFixed(2))}`
        : '';

      return (
        <div>{[amountLabel, paymentMethod].filter(Boolean).join(' ')}</div>
      );
    }
  },
  {
    title: 'Order Status',
    // sortable: true,
    fieldName: 'Status',
    renderer: (data) => {
      return (
        <div
          style={
            changeStatusLoading === data[ORDER.ID] ||
            data[ORDER.ORDER_STATUS] === ORDER_STATUS.CANCELLED
              ? {opacity: '0.5', pointerEvents: 'none'}
              : null
          }
        >
          <StatusButton
            order={data}
            onStatusChange={(status) => {
              onStatusChange(
                data[ORDER.ID],
                status,
                data[ORDER.ORDER_STATUS],
                data[ORDER.DELIVERY]?.isDispatch,
                data[ORDER.SOURCE]
              );
            }}
          />
        </div>
      );
    }
  },
  {
    renderer: (item) => (
      <FlexContainer className='justify-end'>
        <IconButton onClick={() => onPrintClick(item.id)}>
          <IconPrint />
        </IconButton>
      </FlexContainer>
    )
  }
];

const OrdersHistory = ({parentRef, print}) => {
  const dispatch = useDispatch();
  const history = useHistory();

  const locationName = useSelector((state) => state.user.locationName);
  const {
    orders,
    loading,
    filter: {
      startDate,
      endDate,
      orderSource,
      fulfilmentTypes,
      datePickerSelectIndex,
      orderPaymentsMethods
    },
    hasNextPage,
    sorting: {sort, sortBy},
    changeStatusLoading,
    search
  } = useSelector((state) => state.ordersHistoryV2Reducer);

  const getOrders = (isCustomerSearch = false) => {
    dispatch(GET_ORDERS({isCustomerSearch}));
  };
  const {Timezone, AllowedServiceFeatures} = useSelector(
    (state) => state.user.activeLocation
  );
  const showOtterBrandName = AllowedServiceFeatures?.includes(
    FEATURE_RESTRICTION_IDS.OTTER_SHOW_BRAND_NAME
  );

  useEffect(() => {
    dispatch(CLEAR_ORDERS());
    dispatch(SET_SEARCH(''));

    if (!startDate && datePickerSelectIndex) {
      dispatch(
        SET_FILTER({
          startDate: moment()
            .utcOffset(Timezone, false)
            .utc()
            .startOf('isoWeek')
            .format(),
          endDate: moment()
            .utcOffset(Timezone, false)
            .utc()
            .endOf('isoWeek')
            .format()
        })
      );
    }

    getOrders();

    return () => dispatch(CLEAR_ORDERS());
  }, [locationName]);

  const onFilterChange = (filter) => {
    dispatch(CLEAR_ORDERS());
    dispatch(SET_FILTER(filter));
    getOrders();
  };
  const onSortingChange = (sorting) => {
    dispatch(CLEAR_ORDERS());
    dispatch(SET_SORTING(sorting));
    getOrders();
  };

  const onStatusChange = (
    id,
    status,
    previousStatus,
    deliveryProvider,
    source
  ) => {
    if (status !== previousStatus && !changeStatusLoading) {
      if (status === ORDER_STATUS.CANCELLED) {
        dispatch(
          OPEN_CANCELLATION_CONFIRMATION_MODAL_V2({
            isOpen: true,
            info: {id, status, previousStatus, source},
            source: 'history',
            deliveryProvider
          })
        );
      } else {
        dispatch(CHANGE_STATUS_HISTORY({id, status, previousStatus}));
        dispatch(STOP_GET_ACTIVE_ORDERS_JOB());
        dispatch(RUN_GET_ACTIVE_ORDERS_JOB());
      }
    }
  };

  const openDetails = (data) => {
    dispatch(CLEAR_ORDER());
    history.push(`/orders-v2/history/${data[ORDER.ID]}/details`, {
      windowPosition: window.scrollY
    });
  };

  const printOrder = (id) => {
    dispatch(GET_ORDER({id, successCb: print}));
  };

  const updateDate = (date) => {
    if (date && date[0] && date[1]) {
      onFilterChange({
        startDate: date[0],
        endDate: date[1]
      });
    } else {
      onFilterChange({
        startDate: null,
        endDate: null
      });
    }
  };

  const updateSelectedDateIndex = (selectedIndex) => {
    dispatch(SET_FILTER({datePickerSelectIndex: selectedIndex}));
  };

  const debouncedGetOrders = useCallback(
    debounce((isCustomerSearch) => {
      getOrders(isCustomerSearch);
    }, 500),
    []
  );

  const setSearch = (value) => {
    dispatch(SET_SEARCH(value));
    debouncedGetOrders(true);
  };

  const DashboardFilters = ({
    datepickerDate,
    types,
    searchValue,
    setSearchValue
  }) => (
    <>
      <br />
      <FlexContainer justifyContent='flex-end'>
        <FlexItem flex='0.3 1 auto'>
          <SearchField
            placeholder='Search orders by customer name'
            className='marginBottomXs'
            value={searchValue}
            onChange={(e) => {
              setSearchValue(e.target.value);
            }}
            onClear={() => setSearchValue('')}
            isClearIconVisible={searchValue?.length > 0}
          />
        </FlexItem>
      </FlexContainer>
      {/* <DisplayField */}
      {/*  className='marginBottomMd' */}
      {/*  label={<Body1>Now Editing:</Body1>} */}
      {/*  value={<Caption1>{locationName}</Caption1>} */}
      {/* /> */}
      <DashboardFields>
        <DashboardField className='dFlexNoWrapAuto'>
          <DashboardFieldLabel className='OrderFilterLabel'>
            <FieldLabel className='FieldLabelLg'>Filter</FieldLabel>
          </DashboardFieldLabel>
          <DashboardFieldInner className='OrderFilterSelect Say2EatSelect'>
            <FormControl>
              <SelectFiltersOrderV2
                multiple
                MenuProps={{
                  disableScrollLock: true,
                  anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'left'
                  },
                  transformOrigin: {
                    vertical: 'top',
                    horizontal: 'left'
                  },
                  getContentAnchorEl: null
                }}
                name='Provider'
                value={types}
                onChange={({target: {value}}) => {
                  if (
                    value.length === 15 ||
                    value[value.length - 1] === 'All' ||
                    !value.length
                  ) {
                    onFilterChange({
                      fulfilmentTypes: ['All'],
                      orderSource: ['All'],
                      orderPaymentsMethods: ['All']
                    });
                    return;
                  }
                  if (value.length > 1 && value.indexOf('All') !== -1) {
                    value.splice(value.indexOf('All'), 1);
                  }
                  const fulfilmentItem = value.filter((item) =>
                    Object.values(ORDER_FULFILLMENT_NAME).includes(item)
                  );
                  const sourceItem = value.filter((item) =>
                    Object.values(FILTER_ORDER_SOURCE).includes(item)
                  );
                  const paymentsMethodsItem = value.filter((item) =>
                    Object.values(ORDER_PAYMENT_METHODS).includes(item)
                  );
                  onFilterChange({
                    fulfilmentTypes: fulfilmentItem,
                    orderSource: sourceItem,
                    orderPaymentsMethods: paymentsMethodsItem
                  });
                }}
                menuItemsAll={Object.values(ORDER_TYPES_ALL)}
                menuItemsSources={Object.values(FILTER_ORDER_SOURCE)?.filter(
                  (item) => item !== FILTER_ORDER_SOURCE.ALL
                )}
                menuFulfilmentTypes={Object.values(ORDER_FULFILLMENT_NAME)}
                menuItemsPaymentMethod={Object.values(ORDER_PAYMENT_METHODS)}
              />
            </FormControl>
          </DashboardFieldInner>
        </DashboardField>
        <DashboardField className='wAutoSpace dFlex'>
          <DashboardFieldLabel className='widthAuto'>
            <FieldLabel className='FieldLabelLg'>Time period</FieldLabel>
          </DashboardFieldLabel>
          <DashboardFieldInner className='widthWithoutLabel'>
            <DatepickerModule
              date={datepickerDate}
              updateDate={updateDate}
              defaultSelectIndex={datePickerSelectIndex}
              updateSelectedDateIndex={updateSelectedDateIndex}
              updatedAllPeriods='true'
            />
          </DashboardFieldInner>
        </DashboardField>
      </DashboardFields>
    </>
  );

  DashboardFilters.propTypes = {
    datepickerDate: PropTypes.array.isRequired,
    types: PropTypes.array.isRequired,
    searchValue: PropTypes.string.isRequired,
    setSearchValue: PropTypes.func.isRequired
  };

  const renderDashboardFilters = useCallback(
    (date, types, searchValue, setSearchValue) => {
      if (parentRef) {
        return createPortal(
          <DashboardFilters
            datepickerDate={date}
            types={types}
            searchValue={searchValue}
            setSearchValue={setSearchValue}
          />,
          parentRef
        );
      }
      return null;
    },
    [parentRef, AllowedServiceFeatures]
  );
  return (
    <>
      {renderDashboardFilters(
        [startDate, endDate],
        [
          ...new Set([
            ...fulfilmentTypes,
            ...orderSource,
            ...orderPaymentsMethods
          ])
        ],
        search,
        setSearch
      )}
      <InfiniteScroll
        dataLength={orders.length}
        next={getOrders}
        style={{
          display: 'flex',
          justifyContent: 'center',
          flexDirection: 'column',
          overflow: 'inherit'
        }}
        hasMore={hasNextPage}
      >
        <OrdersTableWrapper
          className={cn('OrdersTableWrapper', {
            withOtterBrandName: showOtterBrandName
          })}
        >
          <OrdersTableComponent
            fields={getFieldsConfig(
              onStatusChange,
              openDetails,
              printOrder,
              changeStatusLoading,
              showOtterBrandName,
              AllowedServiceFeatures
            )}
            data={orders}
            loading={loading}
            rowKeyPath={ORDER.ID}
            onSort={onSortingChange}
            orderBy={sortBy}
            orderDirection={sort}
          />
        </OrdersTableWrapper>

        <ButtonScrollTop />
      </InfiniteScroll>
      {hasNextPage && (
        <ButtonPrimary
          onClick={getOrders}
          type='button'
          className={`${loading && 'disabled'} loadMore`}
        >
          <span>Load More</span>
        </ButtonPrimary>
      )}
    </>
  );
};

OrdersHistory.propTypes = {
  parentRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({current: PropTypes.any})
  ]).isRequired,
  print: PropTypes.func.isRequired
};

export default OrdersHistory;
